<template>
  <div>
    <v-data-table
      hide-default-footer
      :headers="headers"
      disable-pagination
      disable-sort
      dense
      v-bind="$attrs"
      v-on="$listeners">
      <template #header.renovation_year1="{ header }">
        <div class="d-flex align-center my-2">
          <span class="v-btn__content">{{ header.text }}</span>
          <v-btn
            color="success"
            class="py-6"
            :disabled="!selectedRenovationYear1"
            @click="onSelectedRenovationYear1(selectedRenovationYear1)">
            {{ selectedRenovationYear1 ? selectedRenovationYear1.id : '' }}行目の内容を<br />全てコピー
          </v-btn>
        </div>
      </template>
      <template #header.renovation_year2="{ header }">
        <div class="d-flex align-center my-2">
          <span class="v-btn__content">{{ header.text }}</span>
          <v-btn
            color="success"
            class="py-6"
            :disabled="!selectedRenovationYear2"
            @click="onSelectedRenovationYear2(selectedRenovationYear2)">
            {{ selectedRenovationYear2 ? selectedRenovationYear2.id : '' }}行目の内容を<br />全てコピー
          </v-btn>
        </div>
      </template>
      <template #body="{ items }">
        <draggable
          :list="items"
          :disabled="readonly"
          tag="tbody"
          ghost-class="ghost"
          @update="updatedTableRow">
          <tr
            v-for="item in items"
            :key="item.id"
            :style="isField && item.disable_field_survey ? 'background-color: gray' : ''">
            <td>
              <v-icon>mdi-drag-horizontal</v-icon>
            </td>
            <td
              v-bind="diffAttributes(getPreviousRoom(item, 'room_no'), item.room_no)">
              <edit-dialog
                v-model="item.room_no"
                :disabled="fieldEditable(item)"
                :error-messages="getErrorMessage(item, 'room_no')"
                label="部屋No">
              </edit-dialog>
              <div
                v-if="hasDiff(getPreviousRoom(item, 'room_no'), item.room_no)">
                変更前: {{ getPreviousRoom(item, 'room_no') }}
              </div>
            </td>
            <td
              v-if="isField"
              v-bind="diffAttributes(
                getPreviousRoom(item, 'field_room_name'), item.field_room_name)">
              <edit-dialog
                v-model="item.field_room_name"
                label="部屋名"
                :disabled="fieldEditable(item)"
                :error-messages="getErrorMessage(item, 'field_room_name')"
                @change="clearErrorMessage(item, 'field_room_name')" />
              <div
                v-if="hasDiff(getPreviousRoom(item, 'field_room_name'), item.field_room_name)">
                変更前: {{ getPreviousRoom(item, 'field_room_name') }}
              </div>
            </td>
            <td
              v-else
              v-bind="diffAttributes(
                getPreviousRoom(item, 'room_name'), item.room_name)">
              <edit-dialog
                v-model="item.room_name"
                label="部屋名"
                :disabled="fieldEditable(item)"
                :error-messages="getErrorMessage(item, 'room_name')"
                @change="clearErrorMessage(item, 'room_name')" />
              <div
                v-if="hasDiff(getPreviousRoom(item, 'room_name'), item.room_name)">
                変更前: {{ getPreviousRoom(item, 'room_name') }}
              </div>
            </td>
            <td
              v-if="isField"
              v-bind="diffAttributes(getPreviousRoom(item, 'field_floors'), item.field_floors)">
              <v-select
                v-model="item.field_floors"
                :items="floors"
                :disabled="fieldEditable(item)"
                menu-props="auto"
                hide-details
                dense
                outlined
                @change="onFloorsChanged" />
              <div
                v-if="hasDiff(getPreviousRoom(item, 'field_floors'), item.field_floors)">
                変更前: {{ getPreviousRoom(item, 'field_floors') }}
              </div>
            </td>
            <td
              v-else
              v-bind="diffAttributes(getPreviousRoom(item, 'floors'), item.floors)">
              <v-select
                v-model="item.floors"
                :items="floors"
                :disabled="fieldEditable(item)"
                menu-props="auto"
                hide-details
                dense
                outlined
                @change="onFloorsChanged" />
              <div
                v-if="hasDiff(getPreviousRoom(item, 'floors'), item.floors)">
                変更前: {{ getPreviousRoom(item, 'floors') }}
              </div>
            </td>
            <td
              v-if="isField"
              v-bind="diffAttributes(
                getPreviousRoom(item, 'field_completion_year'),
                item.field_completion_year)">
              <select-year
                :value="completionYear(item)"
                :disabled="fieldEditable(item)"
                :error-messages="getErrorMessage(item, 'field_completion_year')"
                @input="item.field_completion_year = $event" />
              <div
                v-if="hasDiff(
                  getPreviousRoom(item, 'field_completion_year'), item.field_completion_year)">
                変更前: {{ getPreviousRoom(item, 'field_completion_year') }}
              </div>
            </td>
            <td
              v-else
              v-bind="diffAttributes(
                getPreviousRoom(item, 'completion_year'),
                item.completion_year)">
              <select-year
                v-model="item.completion_year"
                :disabled="fieldEditable(item)"
                :error-messages="getErrorMessage(item, 'completion_year')" />
              <div
                v-if="hasDiff(getPreviousRoom(item, 'completion_year'), item.completion_year)">
                変更前: {{ getPreviousRoom(item, 'completion_year') }}
              </div>
            </td>
            <td
              v-if="isField"
              v-bind="diffAttributes(
                getPreviousRoom(item, 'field_renovation_year1'), item.field_renovation_year1)">
              <select-year
                v-model="item.field_renovation_year1"
                :disabled="fieldEditable(item)"
                :error-messages="getErrorMessage(item, 'field_renovation_year1')"
                :class="{ 'selected-row': item === selectedRenovationYear1 }"
                @click="select1Row(item)" />
              <div
                v-if="hasDiff(
                  getPreviousRoom(item, 'field_renovation_year1'), item.field_renovation_year1)">
                変更前: {{ getPreviousRoom(item, 'field_renovation_year1') }}
              </div>
            </td>
            <td
              v-else
              v-bind="diffAttributes(
                getPreviousRoom(item, 'renovation_year1'), item.renovation_year1)">
              <select-year
                v-model="item.renovation_year1"
                :disabled="fieldEditable(item)"
                :error-messages="getErrorMessage(item, 'renovation_year1')"
                :class="{ 'selected-row': item === selectedRenovationYear1 }"
                @click="select1Row(item)" />
              <div
                v-if="hasDiff(getPreviousRoom(item, 'renovation_year1'), item.renovation_year1)">
                変更前: {{ getPreviousRoom(item, 'renovation_year1') }}
              </div>
            </td>
            <td
              v-if="isField"
              v-bind="diffAttributes(
                getPreviousRoom(item, 'field_renovation_year2'), item.field_renovation_year2)">
              <select-year
                v-model="item.field_renovation_year2"
                :disabled="fieldEditable(item)"
                :error-messages="getErrorMessage(item, 'field_renovation_year2')"
                :class="{ 'selected-row': item === selectedRenovationYear2 }"
                @click="select2Row(item)" />
              <div
                v-if="hasDiff(
                  getPreviousRoom(item, 'field_renovation_year2'), item.field_renovation_year2)">
                変更前: {{ getPreviousRoom(item, 'field_renovation_year2') }}
              </div>
            </td>
            <td
              v-else
              v-bind="diffAttributes(
                getPreviousRoom(item, 'renovation_year2'), item.renovation_year2)">
              <select-year
                v-model="item.renovation_year2"
                :disabled="fieldEditable(item)"
                :error-messages="getErrorMessage(item, 'renovation_year2')"
                :class="{ 'selected-row': item === selectedRenovationYear2 }"
                @click="select2Row(item)" />
              <div
                v-if="hasDiff(getPreviousRoom(item, 'renovation_year2'), item.renovation_year2)">
                変更前: {{ getPreviousRoom(item, 'renovation_year2') }}
              </div>
            </td>
            <td>
              <div class="d-inline-flex">
                <template v-if="isField">
                  <v-btn
                    v-if="removable || (!removable && !item.id)"
                    fab
                    dark
                    x-small
                    color="red"
                    @click="onDeletionButtonClicked(item)">
                    <v-icon dark>mdi-minus</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="removable || (!removable && !item.id)"
                    fab
                    dark
                    x-small
                    :color="item.disable_field_survey ? 'orange' : 'green'"
                    @click="onDisableClicked(item)">
                    <v-icon v-if="item.disable_field_survey">mdi-eye-off</v-icon>
                    <v-icon v-else>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn
                    v-if="removable || (!removable && !item.id)"
                    fab
                    dark
                    x-small
                    color="red"
                    @click="onDeletionButtonClicked(item)">
                    <v-icon dark>mdi-minus</v-icon>
                  </v-btn>
                </template>
                <v-btn
                  v-if="removable || (!removable && !item.id)"
                  fab
                  dark
                  x-small
                  color="primary"
                  @click="onAdditionClicked(item)">
                  <v-icon dark>mdi-plus</v-icon>
                </v-btn>
              </div>
            </td>
          </tr>
        </draggable>
      </template>
    </v-data-table>
    <v-card-actions>
      <v-spacer />
      <v-btn
        v-if="!readonly"
        large
        color="primary"
        @click.prevent="onAddRowButtonClicked">
        部屋情報を追加する
      </v-btn>
      <v-spacer />
    </v-card-actions>
    <confirm-dialog
      v-if="confirmDialog"
      v-model="confirmDialog"
      title="確認"
      @ok="onDeletionConfirmOk"
      @cancel="confirmDialog=false">
      削除します。よろしいですか？
      <v-alert color="warning">
        この部屋に対して書面調査、現地調査の情報が記入済みの場合削除されます。
        この操作は元に戻せません。
      </v-alert>
    </confirm-dialog>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import EditDialog from './EditDialog'
import SelectYear from './SelectYear'
import ConfirmDialog from './ConfirmDialog'
import PreviousDiff from '../mixins/PreviousDiff'

export default {
  name: 'RoomTable',
  components: {
    Draggable,
    SelectYear,
    EditDialog,
    ConfirmDialog,
  },
  mixins: [PreviousDiff],
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    removable: {
      type: Boolean,
      default: true,
    },
    isField: {
      type: Boolean,
      default: false,
    },
    created: {
      type: Boolean,
      default: false,
    },
    applied: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      default: () => ([
        { text: '', value: 'drag', width: '3%' },
        { text: '部屋No', value: 'room_no', width: '10%' },
        { text: '部屋名', value: 'room_name', width: '20%' },
        { text: '階数', value: 'floors', width: '7%' },
        { text: '竣工年', value: 'completion_year', width: '17%' },
        { text: '改修年①', value: 'renovation_year1', width: '17%' },
        { text: '改修年②', value: 'renovation_year2', width: '17%' },
        { text: '', value: 'delete', width: '9%' },
      ]),
    },
    errorMessages: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      confirmDialog: false,
      deletionTargetItem: null,
      selectedRenovationYear1: null,
      selectedRenovationYear2: null,
    }
  },
  computed: {
    completionYear: () => item => item.field_completion_year ?? item.completion_year,
    floors() {
      const floors = []
      for (let i = -10; i <= Number(this.$attrs.floor) ?? 30; i += 1) {
        if (i !== 0) {
          floors.push({
            text: (i > 0) ? `${i}F` : `B${Math.abs(i)}`,
            value: i,
          })
        }
      }
      floors.push({
        text: 'RF',
        value: 99,
      })
      return floors
    },
  },
  methods: {
    updatedTableRow(event) {
      this.$emit('replace', event.oldIndex, event.newIndex)
    },
    onDeletionButtonClicked(item) {
      if (item.id) {
        this.confirmDialog = true
        this.deletionTargetItem = item
      } else {
        this.deletionTargetItem = item
        this.onDeletionConfirmOk()
      }
    },
    onAutoNumberClicked() {
      this.autoNumberConfirmDialog = true
    },
    onAdditionClicked(item) {
      const index = this.$attrs.items.indexOf(item)
      this.$emit('insert', index)
    },
    onAddRowButtonClicked() {
      const index = this.$attrs.items.length - 1
      this.$emit('insert', index)
    },
    onFloorsChanged() {
      this.$emit('update')
    },
    onDeletionConfirmOk() {
      const index = this.$attrs.items.indexOf(this.deletionTargetItem)
      this.$emit('remove', index)
      this.confirmDialog = false
    },

    onDisableClicked(item) {
      const index = this.$attrs.items.indexOf(item)
      if (item.disable_field_survey) {
        this.$emit('enable', index)
      } else {
        this.$emit('disable', index)
      }
    },

    select1Row(item) {
      this.selectedRenovationYear1 = item
    },
    onSelectedRenovationYear1() {
      this.$emit('updateFirst', this.selectedRenovationYear1.sort_index - 1)
    },
    select2Row(item) {
      this.selectedRenovationYear2 = item
    },
    onSelectedRenovationYear2() {
      this.$emit('updateSecond', this.selectedRenovationYear2.sort_index - 1)
    },

    getErrorMessage(item, columnName) {
      const index = this.$attrs.items.indexOf(item)
      return this.errorMessages[`rooms.${index}.${columnName}`]
    },
    clearErrorMessage(item, columnName) {
      const index = this.$attrs.items.indexOf(item)
      this.$delete(this.errorMessages, `rooms.${index}.${columnName}`)
    },
    fieldEditable(item) {
      return this.isField
        ? (this.readonly && !!item.id) || !!item.disable_field_survey
        : this.readonly && !!item.id
    },
    getPreviousRoom(object, columnName) {
      if (!this.previousObject || !this.previousObject.rooms) {
        return null
      }
      const target = this.previousObject.rooms.filter(item => item.id === object.id)[0] ?? null
      if (!target) {
        return ' '
      }
      const index = this.previousObject.rooms.indexOf(target)
      return this.previousObject && this.previousObject.rooms
        ? this.previousObject.rooms[index][columnName] ?? null
        : null
    },
    hasDiff(previous, current) {
      return this.created && this.applied
        ? this.$super(PreviousDiff).hasDiff(previous, current)
        : false
    },
  },
}
</script>

<style scoped>
.ghost {
  background-color: red;
  transform:scaleY(0.05)!important;
}

.selected-row {
  background-color: #e0e0e0;
}

.header-th {
  border-bottom:1px solid;
  font-size: 12px;
  color: #00000099;
  text-align: start;
  padding: 0px 16px 0px;
}
</style>
